import { type ReactNode } from 'react';
import { LoadingButton } from './LoadingSpinner';
import { 
  AppErrorClass, 
  AuthErrorClass, 
  DatabaseErrorClass,
  getErrorAction,
  isRecoverableError 
} from '../../utils/errorHandling';

interface ErrorDisplayProps {
  error: Error;
  onRetry?: () => void;
  onSignIn?: () => void;
  onContactSupport?: () => void;
  retrying?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
}

export function ErrorDisplay({
  error,
  onRetry,
  onSignIn,
  onContactSupport,
  retrying = false,
  className = '',
  size = 'md',
  showDetails = false,
}: ErrorDisplayProps) {
  const { action, message: actionMessage } = getErrorAction(error);
  const recoverable = isRecoverableError(error);

  const sizeClasses = {
    sm: 'p-4 text-sm',
    md: 'p-6 text-base',
    lg: 'p-8 text-lg',
  };

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  const getErrorIcon = () => {
    if (error instanceof AuthErrorClass) {
      return (
        <svg className={`${iconSizes[size]} text-yellow-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    }

    if (error instanceof DatabaseErrorClass) {
      return (
        <svg className={`${iconSizes[size]} text-blue-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
      );
    }

    return (
      <svg className={`${iconSizes[size]} text-red-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    );
  };

  const getErrorTitle = () => {
    if (error instanceof AuthErrorClass) {
      return 'Authentication Required';
    }
    if (error instanceof DatabaseErrorClass) {
      return 'Data Unavailable';
    }
    return 'Something went wrong';
  };

  const renderActionButton = () => {
    switch (action) {
      case 'retry':
        return onRetry ? (
          <LoadingButton
            loading={retrying}
            onClick={onRetry}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Try Again
          </LoadingButton>
        ) : null;

      case 'signin':
        return onSignIn ? (
          <LoadingButton
            loading={false}
            onClick={onSignIn}
            className="bg-yellow-600 text-white hover:bg-yellow-700"
          >
            Sign In
          </LoadingButton>
        ) : null;

      case 'contact_support':
        return onContactSupport ? (
          <LoadingButton
            loading={false}
            onClick={onContactSupport}
            className="bg-gray-600 text-white hover:bg-gray-700"
          >
            Contact Support
          </LoadingButton>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md ${sizeClasses[size]} ${className}`}>
      <div className="text-center">
        <div className="flex justify-center mb-4">
          {getErrorIcon()}
        </div>
        
        <h3 className="font-semibold text-gray-800 mb-2">
          {getErrorTitle()}
        </h3>
        
        <p className="text-gray-600 mb-4">
          {error.message}
        </p>

        {actionMessage && (
          <p className="text-sm text-gray-500 mb-4">
            {actionMessage}
          </p>
        )}

        {showDetails && error instanceof AppErrorClass && error.details && (
          <details className="mb-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Show technical details
            </summary>
            <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-auto">
              {JSON.stringify(error.details, null, 2)}
            </pre>
          </details>
        )}

        <div className="flex justify-center space-x-3">
          {renderActionButton()}
          
          {recoverable && onRetry && action !== 'retry' && (
            <LoadingButton
              loading={retrying}
              onClick={onRetry}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Retry
            </LoadingButton>
          )}
        </div>
      </div>
    </div>
  );
}

interface InlineErrorProps {
  error: Error;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
}

export function InlineError({ 
  error, 
  onRetry, 
  retrying = false, 
  className = '' 
}: InlineErrorProps) {
  return (
    <div className={`flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-md ${className}`}>
      <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm text-red-800 truncate">
          {error.message}
        </p>
      </div>
      
      {onRetry && (
        <LoadingButton
          loading={retrying}
          onClick={onRetry}
          className="text-xs bg-red-100 text-red-800 hover:bg-red-200 px-2 py-1"
        >
          Retry
        </LoadingButton>
      )}
    </div>
  );
}

interface ErrorAlertProps {
  error: Error;
  onDismiss?: () => void;
  className?: string;
}

export function ErrorAlert({ error, onDismiss, className = '' }: ErrorAlertProps) {
  const getAlertColor = () => {
    if (error instanceof AuthErrorClass) return 'yellow';
    if (error instanceof DatabaseErrorClass) return 'blue';
    return 'red';
  };

  const color = getAlertColor();
  
  const colorClasses = {
    red: 'bg-red-50 border-red-200 text-red-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`border rounded-md p-4 ${colorClasses[color]} ${className}`}>
      <div className="flex items-start">
        <div className="flex-1">
          <p className="text-sm font-medium">
            {error.message}
          </p>
        </div>
        
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-3 flex-shrink-0 hover:opacity-75"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  icon?: ReactNode;
  className?: string;
}

export function EmptyState({ 
  title, 
  description, 
  action, 
  icon, 
  className = '' 
}: EmptyStateProps) {
  const defaultIcon = (
    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  return (
    <div className={`text-center py-12 ${className}`}>
      <div className="flex justify-center mb-4">
        {icon || defaultIcon}
      </div>
      
      <h3 className="text-lg font-medium text-gray-800 mb-2">
        {title}
      </h3>
      
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        {description}
      </p>
      
      {action && (
        <LoadingButton
          loading={action.loading || false}
          onClick={action.onClick}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          {action.label}
        </LoadingButton>
      )}
    </div>
  );
}